/**
 * Allys Team Section Component
 * 
 * Premium styled team member display with Allys design language
 */

'use client';

import { motion } from 'framer-motion';
import Image from 'next/image';
import type { TeamSectionProps } from '@routeroyal/theme-core';

/**
 * Allys Team Section - Elegant team member showcase
 */
export function AllysTeamSection({
  title,
  subtitle,
  members,
  layout = 'grid'
}: TeamSectionProps) {
  return (
    <section className="py-24 lg:py-32 bg-white relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.015] bg-[radial-gradient(circle_at_50%_50%,var(--color-primary)_1px,transparent_1px)] bg-[length:40px_40px]" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-20">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6 tracking-tight [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]"
          >
            {title}
          </motion.h2>
          
          {subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto font-light [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]"
            >
              {subtitle}
            </motion.p>
          )}
        </div>
        
        {/* Team Grid */}
        {layout === 'grid' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-14">
            {members.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.8, delay: index * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="group"
              >
                {/* Photo */}
                {member.image && (
                  <div className="relative aspect-square mb-6 rounded-2xl overflow-hidden bg-gray-100 shadow-lg group-hover:shadow-2xl transition-shadow duration-500">
                    <Image
                      src={member.image}
                      alt={member.name}
                      fill
                      className="object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                      sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    />
                  </div>
                )}
                
                {/* Info */}
                <h3 className="text-2xl lg:text-3xl font-heading font-bold text-gray-900 mb-2 tracking-tight [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
                  {member.name}
                </h3>
                <p className="text-base lg:text-lg text-gray-600 mb-4 font-medium [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
                  {member.role}
                </p>
                
                {member.bio && (
                  <p className="text-sm lg:text-base text-gray-600 leading-relaxed mb-5 font-light [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
                    {member.bio}
                  </p>
                )}
                
                {/* Social Links */}
                {member.social && (
                  <div className="flex gap-3">
                    {member.social.linkedin && (
                      <a
                        href={member.social.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-gray-900 transition-colors duration-200"
                        aria-label="LinkedIn"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                        </svg>
                      </a>
                    )}
                    {member.social.email && (
                      <a
                        href={`mailto:${member.social.email}`}
                        className="text-gray-400 hover:text-gray-900 transition-colors duration-200"
                        aria-label="Email"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </a>
                    )}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
        
        {/* Featured Layout */}
        {layout === 'featured' && members.length > 0 && (() => {
          const featuredMember = members[0];
          if (!featuredMember) return null;
          
          return (
            <div className="space-y-20">
              {/* Featured Member */}
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center"
              >
                {featuredMember.image && (
                  <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-gray-100 shadow-2xl">
                    <Image
                      src={featuredMember.image}
                      alt={featuredMember.name}
                      fill
                      className="object-cover"
                      sizes="(max-width: 1024px) 100vw, 50vw"
                      priority
                    />
                  </div>
                )}
                
                <div>
                  <h3 className="text-4xl lg:text-5xl font-heading font-bold text-gray-900 mb-4 tracking-tight [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
                    {featuredMember.name}
                  </h3>
                  <p className="text-xl lg:text-2xl text-gray-600 mb-8 font-medium [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
                    {featuredMember.role}
                  </p>
                  {featuredMember.bio && (
                    <p className="text-lg lg:text-xl text-gray-600 leading-relaxed font-light [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
                      {featuredMember.bio}
                    </p>
                  )}
                </div>
                </motion.div>
              
              {/* Other Members */}
              {members.length > 1 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                {members.slice(1).map((member, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-100px' }}
                    transition={{ duration: 0.8, delay: index * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
                    className="group"
                  >
                    {member.image && (
                      <div className="relative aspect-square mb-5 rounded-xl overflow-hidden bg-gray-100 shadow-md group-hover:shadow-xl transition-shadow duration-500">
                        <Image
                          src={member.image}
                          alt={member.name}
                          fill
                          className="object-cover group-hover:scale-105 transition-transform duration-700"
                          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                        />
                      </div>
                    )}
                    <h4 className="text-xl lg:text-2xl font-heading font-bold text-gray-900 mb-2 [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
                      {member.name}
                    </h4>
                    <p className="text-base text-gray-600 font-medium [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
                      {member.role}
                    </p>
                  </motion.div>
                ))}
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </section>
  );
}

